import { useState } from 'react'
import type { StorefrontPlan, StorefrontRun, SyncRow } from '@fitting-room/contracts'
import { AlertTriangle, Check, Globe, RefreshCw } from 'lucide-react'
import { HelpButton, StorefrontHelp } from '../../components/admin/HelpDrawer'
import { PageHeader } from '../../components/admin/PageHeader'
import { usePlanStorefront, usePublishStorefront, useStorefrontRun } from '../../lib/adminQueries'
import { useStaffToken } from '../../lib/staffToken'

/**
 * Publish to the website - the reviewed catalog, pushed to the boutique's own
 * storefront.
 *
 * Two steps, and the split is the whole screen, exactly as it is on "Sync
 * gowns". What is different is what the red rows mean. There, a retirement
 * takes a gown off the boutique's own deck and a stylist can put it back on
 * the Catalog page that afternoon. Here, a removal takes a PAGE down: the
 * address stops resolving for anyone who saved it, and the search engine that
 * spent months indexing it starts dropping it. So the removals are listed
 * first, never truncated, and said in those words rather than in the word
 * "retired".
 */

/**
 * A failure said to the person standing in front of it.
 *
 * Same shape and same reasoning as the sync console's: the message names the
 * missing setting or the count that looked wrong, and the error code stays out
 * of it because `(storefront_not_configured)` is a thing to grep the source
 * for, not a thing to read at work.
 */
function PublishError({ message }: { readonly message: string }) {
  return (
    <div className="st-alert mb-5">
      <p>{message}</p>
      <p className="mt-1 text-xs opacity-70">If this keeps happening, please contact your system developer.</p>
    </div>
  )
}

/**
 * How many unremarkable rows the table prints before summarising the rest.
 *
 * The same 25 the sync console uses, for the same reason and with the same
 * exception: it applies to new and changed rows only. The removals are never
 * truncated at any length, because they are the reason this screen exists.
 */
const ROWS_SHOWN = 25

/**
 * The field keys the plan reports, in the words the gown page uses. The keys
 * are the storefront's (`velvetHourStorefront.fields`); anything not listed
 * here is shown as it is, which is still readable.
 */
const FIELD_WORDS: Readonly<Record<string, string>> = {
  title: 'name',
  style_code: 'style number',
  description: 'description',
  collection: 'collection',
  silhouette: 'silhouette',
  neckline: 'neckline',
  detail: 'details',
  'photo order': 'photo order',
}

/** "description, neckline, +2 photos" - one line saying exactly what moves. */
function whatChanged(row: SyncRow): string {
  const parts = row.fields.map((field) => FIELD_WORDS[field] ?? field)
  if (row.photosAdded > 0) parts.push(`+${row.photosAdded} ${row.photosAdded === 1 ? 'photo' : 'photos'}`)
  if (row.photosRemoved > 0) parts.push(`\u2212${row.photosRemoved} ${row.photosRemoved === 1 ? 'photo' : 'photos'}`)
  return parts.join(', ')
}

function photoNote(row: SyncRow): string {
  const parts: string[] = []
  if (row.photosAdded > 0) parts.push(`${row.photosAdded} ${row.photosAdded === 1 ? 'photo' : 'photos'}`)
  return parts.join(' ')
}

function PlanTable({ plan }: { readonly plan: StorefrontPlan }) {
  const created = plan.created.slice(0, ROWS_SHOWN)
  const updated = plan.updated.slice(0, ROWS_SHOWN)

  return (
    <div className="st-card overflow-x-auto">
      <table className="st-table">
        <thead>
          <tr>
            <th>Style</th>
            <th>Change</th>
            <th>What</th>
          </tr>
        </thead>
        <tbody>
          {/* First, and never truncated: the only rows on this screen that
              take a public address away. */}
          {plan.removed.map((row) => (
            <tr key={`removed-${row.ref}`} data-tone="alert">
              <td className="font-medium">{row.label}</td>
              <td>
                <span className="st-badge st-badge--alert">Page comes down</span>
              </td>
              <td className="st-meta">no longer active in the catalog; its address stops working</td>
            </tr>
          ))}

          {created.map((row) => (
            <tr key={`new-${row.ref}`}>
              <td className="font-medium">{row.label}</td>
              <td>
                <span className="st-badge st-badge--submitted">New page</span>
              </td>
              <td className="st-meta">{photoNote(row) || 'no photos yet'}</td>
            </tr>
          ))}
          {plan.created.length > created.length ? (
            <tr>
              <td className="st-meta" colSpan={3}>
                ...and {plan.created.length - created.length} more new pages.
              </td>
            </tr>
          ) : null}

          {updated.map((row) => (
            <tr key={`changed-${row.ref}`}>
              <td className="font-medium">{row.label}</td>
              <td>
                <span className="st-badge st-badge--open">Updated</span>
              </td>
              <td>{whatChanged(row)}</td>
            </tr>
          ))}
          {plan.updated.length > updated.length ? (
            <tr>
              <td className="st-meta" colSpan={3}>
                ...and {plan.updated.length - updated.length} more updated pages.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The publish, while it is happening and after it has.
 *
 * Read from the run record rather than from what the button returned, because
 * the run outlives the tab that started it.
 */
function RunPanel({ run }: { readonly run: StorefrontRun }) {
  if (run.status === 'failed') {
    /*
     * What it says depends on how far it got, because "nothing was published"
     * is a claim and not a comfort. Both guards run before a single record is
     * written, so a failure while reading really did write nothing; a failure
     * while pushing did not, and saying otherwise is how a half-published
     * website goes unnoticed.
     */
    const wrote =
      run.phase === 'reading'
        ? 'Nothing was published.'
        : 'This stopped while publishing, so part of it may have gone out. Plan again to see where it got to.'
    return (
      <div className="st-alert mb-5">
        <p className="font-medium">{wrote}</p>
        <p className="mt-1">{run.error}</p>
      </div>
    )
  }

  if (run.status === 'running') {
    return (
      <div className="st-notice mb-5">
        <p className="font-medium">
          {run.phase === 'reading' ? 'Reading the catalog and the website...' : 'Publishing...'}
        </p>
        <p className="mt-1 tabular-nums">
          {run.counts.created} new, {run.counts.updated} updated, {run.counts.removed} taken down
        </p>
        <p className="st-meta mt-1">
          This keeps running if you close the tab. Come back to this page to see where it got to.
        </p>
      </div>
    )
  }

  /*
   * `failed` here is not the run failing - it is records the storefront
   * refused one at a time while the rest went out fine. It gets the alert tone
   * rather than the success tone precisely because the headline says "Published"
   * and a number of missing gowns underneath it is the thing nobody would
   * otherwise look at.
   */
  return (
    <div className={run.counts.failed > 0 ? 'st-alert mb-5' : 'st-ok mb-5'}>
      <p className="font-medium">Published.</p>
      <p className="mt-1 tabular-nums">
        {run.counts.created} new pages, {run.counts.updated} updated, {run.counts.removed} taken down.
        {run.counts.failed > 0 ? ` ${run.counts.failed} could not be published.` : ''}
      </p>
      {run.warnings.length > 0 ? (
        <details className="mt-2" open={run.counts.failed > 0}>
          <summary className="cursor-pointer">{run.warnings.length} warnings</summary>
          <ul className="mt-1 list-disc pl-5">
            {run.warnings.slice(0, 20).map((warning, index) => (
              <li key={`${index}-${warning}`}>{warning}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}

/**
 * One number, big, with its name under it. Three of them say what a publish
 * would do before a single row is read, which is what somebody who came here
 * to find out "did anything change?" actually needs.
 */
function Stat({ label, value, tone }: { readonly label: string; readonly value: number; readonly tone?: 'good' | 'open' | 'alert' }) {
  // The same inks `.st-alert` and `.st-ok` use, so a red 3 here and the red
  // rows below it are the same red.
  const color = tone === 'alert' ? '#8d3f2c' : tone === 'good' ? '#3c5c37' : undefined
  return (
    <div className="st-card p-4">
      <div className="st-display text-4xl tabular-nums" style={color ? { color } : undefined}>
        {value}
      </div>
      <div className="st-meta mt-1">{label}</div>
    </div>
  )
}

export function StorefrontPage() {
  const { token, role } = useStaffToken()
  // Drawing only. The endpoint is the gate; see the note beside the button.
  const canPublish = role === 'owner'
  const plan = usePlanStorefront(token)
  const publish = usePublishStorefront(token)
  const runQuery = useStorefrontRun(token)
  const [result, setResult] = useState<StorefrontPlan | null>(null)

  const run = runQuery.data?.run ?? null
  const busy = run?.status === 'running'
  // The table describes the website as it was when it was read. Once a publish
  // has changed it, every count on screen is about a past state, so the
  // comparison is dropped rather than left sitting there looking current.
  const published = publish.isSuccess && run?.status === 'succeeded'
  const changes = result ? result.created.length + result.updated.length + result.removed.length : 0

  /*
   * A licensee with no storefront is not a broken deployment, and this is the
   * screen that has to say so out loud. `configured` rides on the run query,
   * so the answer arrives on first load rather than as a 503 the page would
   * have to interpret.
   */
  if (runQuery.data && !runQuery.data.configured) {
    return (
      <div>
        <PageHeader eyebrow="To the boutique's website" title="Publish">
          <HelpButton title="From Airtable to the website">
            <StorefrontHelp />
          </HelpButton>
        </PageHeader>
        <div className="st-card px-4 py-10 text-center">
          <Globe size={20} aria-hidden className="mx-auto mb-2 opacity-40" />
          <p className="st-meta">
            This boutique has no storefront connected, so there is nowhere to publish to.
          </p>
          <p className="st-meta mt-1">
            The catalog, the appointments and everything else on this console work exactly as they do.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader eyebrow="To the boutique's website" title="Publish">
        <HelpButton title="From Airtable to the website">
          <StorefrontHelp />
        </HelpButton>
        <button
          type="button"
          className="st-btn-ghost"
          disabled={plan.isPending || busy}
          onClick={() => {
            publish.reset()
            plan.mutate(undefined, { onSuccess: setResult })
          }}
        >
          <RefreshCw size={14} aria-hidden />
          {plan.isPending ? 'Checking...' : result ? 'Check again' : 'Check for changes'}
        </button>
      </PageHeader>

      <p className="st-meta mb-5 max-w-2xl">
        Checking compares the catalog with the website and changes nothing. If they differ, you see exactly which
        gowns and what on them, and a button to publish those changes. Only gowns marked active go on the website.
      </p>

      {plan.isError ? <PublishError message={plan.error.message} /> : null}
      {publish.isError ? <PublishError message={publish.error.message} /> : null}

      {run ? <RunPanel run={run} /> : null}

      {result && !published ? (
        <>
          {/*
           * A refusal, not a warning with a way past it. Both of them mean the
           * catalog cannot be believed, and there is no version of "take 200
           * pages off your website" that should be one click away.
           */}
          {result.refusals.map((refusal) => (
            <div key={refusal.code} className="st-alert mb-4">
              <p className="flex items-center gap-2 font-medium">
                <AlertTriangle size={15} aria-hidden />
                This plan cannot be published
              </p>
              <p className="mt-1">{refusal.message}</p>
            </div>
          ))}

          {/*
           * Gowns that will simply not appear. Shown BEFORE the button rather
           * than in the run afterwards, because a gown missing from a website
           * is not something anyone notices by looking at the website.
           */}
          {result.warnings.length > 0 ? (
            <div className="st-notice mb-4">
              <p className="font-medium">
                {result.warnings.length} {result.warnings.length === 1 ? 'gown' : 'gowns'} will not be published
              </p>
              <ul className="mt-1 list-disc pl-5">
                {result.warnings.slice(0, 20).map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {changes === 0 ? (
            <div className="st-ok mb-5">
              <p className="flex items-center gap-2 font-medium">
                <Check size={15} aria-hidden />
                The website is up to date.
              </p>
              <p className="mt-1 tabular-nums">
                {result.liveRows} {result.liveRows === 1 ? 'gown' : 'gowns'} on the website, all as the catalog has them. Nothing to publish.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 grid grid-cols-3 gap-3">
                <Stat label="New pages" value={result.created.length} tone={result.created.length > 0 ? 'good' : undefined} />
                <Stat label="Updated pages" value={result.updated.length} tone={result.updated.length > 0 ? 'open' : undefined} />
                <Stat label="Pages coming down" value={result.removed.length} tone={result.removed.length > 0 ? 'alert' : undefined} />
              </div>

              <div className="mb-6 flex flex-wrap items-center gap-3">
                {/*
                  A stylist can read this whole page and cannot press this button.
                  That split is deliberate - see `OWNER_ONLY` in the contracts
                  package - and she is TOLD so rather than shown a control that
                  answers 403, because a button that refuses is indistinguishable
                  from a button that is broken.
                */}
                {canPublish ? (
                  <button
                    type="button"
                    className="st-btn"
                    disabled={!result.applicable || busy || publish.isPending}
                    onClick={() => publish.mutate({ planId: result.planId })}
                  >
                    Publish {changes} {changes === 1 ? 'change' : 'changes'}
                  </button>
                ) : (
                  <span className="st-meta">
                    Publishing is the owner&apos;s to press. Send them this page, or ask them to sign in here.
                  </span>
                )}
                {result.removed.length > 0 ? (
                  <span className="st-meta">
                    The {result.removed.length === 1 ? 'page' : 'pages'} in red will come down. Their addresses stop working for anyone who saved them.
                  </span>
                ) : null}
              </div>
            </>
          )}

          {changes > 0 ? <PlanTable plan={result} /> : null}
        </>
      ) : null}

      {!result && !run ? (
        <div className="st-card px-4 py-10 text-center">
          <p className="st-meta">Check for changes to see whether the website is behind the catalog.</p>
        </div>
      ) : null}
    </div>
  )
}
